import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Clock, ChevronDown, ChevronUp, 
  RefreshCw, ExternalLink, List
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Categorize play type for styling (outside component)
const categorizePlay = (description) => {
  const desc = description.toLowerCase();
  if (desc.includes('touchdown') || desc.includes('goal') || desc.includes('score')) return 'scoring';
  if (desc.includes('turnover') || desc.includes('fumble') || desc.includes('interception') || desc.includes('steal')) return 'turnover';
  if (desc.includes('penalty') || desc.includes('foul')) return 'penalty';
  if (desc.includes('timeout')) return 'timeout';
  if (desc.includes('kickoff') || desc.includes('punt')) return 'special';
  return 'normal';
};

// Parse StatMoose play-by-play data (outside component - pure function)
const parseStatMoosePlays = (gameData, sportType) => {
  const plays = [];
  const pbpData = gameData.play_by_play || gameData.plays || gameData.pbp || [];
  
  // Handle different StatMoose formats
  if (Array.isArray(pbpData)) {
    pbpData.forEach((play, idx) => {
      plays.push({
        id: play.id || idx,
        time: play.time || play.clock || play.game_clock || "",
        period: play.period || play.quarter || play.half || "",
        team: play.team || play.team_abbr || play.team_name || "",
        teamColor: play.team_color || null,
        description: play.description || play.text || play.play_text || play.narrative || "",
        type: play.type || play.play_type || categorizePlay(play.description || play.text || ""),
        isScoring: play.is_scoring || play.scoring || false,
        points: play.points || 0,
        fieldPosition: play.field_position || play.location || "",
        downDistance: play.down_distance || play.down || "",
        result: play.result || ""
      });
    });
  }
  
  // Sort by most recent first
  return plays.reverse();
};

/**
 * PlayByPlayWidget - Universal play-by-play display for all sports
 * Fetches and displays live play-by-play data from StatMoose, PrestoSports, etc.
 */
export const PlayByPlayWidget = ({ 
  statsUrl, 
  sport = "basketball",
  isLive = true, 
  homeTeam, 
  awayTeam,
  maxPlays = 10,
  autoRefresh = true
}) => {
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // Detect provider from URL
  const detectProvider = useCallback((url) => {
    if (!url) return null;
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('statmoose.com')) return 'statmoose';
    if (urlLower.includes('prestosports.com') || urlLower.includes('.xml')) return 'presto';
    if (urlLower.includes('sidearm')) return 'sidearm';
    
    return 'statmoose'; // Default
  }, []);

  // Extract game ID from URL
  const extractGameId = useCallback((url) => {
    if (!url) return null;
    
    // StatMoose football: /football/{game-id}/stats
    const footballMatch = url.match(/\/football\/([a-f0-9-]+)/i);
    if (footballMatch) return footballMatch[1];
    
    // StatMoose basketball: /basketball/{game-id}/stats or /live/{code}
    const basketballMatch = url.match(/\/(?:basketball|live)\/([a-zA-Z0-9-]+)/i);
    if (basketballMatch) return basketballMatch[1];
    
    // Generic: last path segment
    const parts = url.split('/').filter(p => p && p !== 'stats');
    return parts[parts.length - 1] || null;
  }, []);

  // Fetch play-by-play data
  const fetchPlays = useCallback(async () => {
    if (!statsUrl) {
      setError("No stats URL provided");
      setLoading(false);
      return;
    }

    const provider = detectProvider(statsUrl);
    const gameId = extractGameId(statsUrl);

    try {
      let data = null;

      if (provider === 'statmoose') {
        // Fetch through our backend proxy (avoids CORS/firewall blocks)
        const response = await fetch(`${API}/broadcasts/statmoose/proxy/${gameId}`);
        if (response.ok) {
          const gameData = await response.json();
          if (!gameData.error) {
            data = parseStatMoosePlays(gameData, sport);
          }
        }
      } else if (provider === 'presto') {
        // Fetch from our backend proxy
        const response = await fetch(`${API}/livestats/playbyplay?url=${encodeURIComponent(statsUrl)}`);
        if (response.ok) {
          const pbpData = await response.json();
          data = pbpData.plays || [];
        }
      }

      if (data && data.length > 0) {
        setPlays(data);
        setLastUpdate(new Date());
        setError(null);
      } else if (!data) {
        setError("Could not fetch play-by-play data");
      }
    } catch (err) {
      console.error("Error fetching play-by-play:", err);
      setError("Failed to load plays");
    } finally {
      setLoading(false);
    }
  }, [statsUrl, sport, detectProvider, extractGameId]);

  useEffect(() => {
    if (!statsUrl) return;
    
    fetchPlays();
    
    // Auto-refresh for live games
    if (isLive && autoRefresh) {
      const interval = setInterval(fetchPlays, 10000);
      return () => clearInterval(interval);
    }
  }, [statsUrl, isLive, autoRefresh, fetchPlays]);

  if (!statsUrl) return null;

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4" data-testid="pbp-loading">
        <div className="flex items-center justify-center gap-2 text-zinc-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading play-by-play...</span>
        </div>
      </div>
    );
  }

  if (error || plays.length === 0) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4" data-testid="pbp-external">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <List className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">PLAY-BY-PLAY</span>
          </div>
          <a 
            href={statsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-orange-500 hover:text-orange-400 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            View Full Play-by-Play
          </a>
        </div>
      </div>
    );
  }

  const displayedPlays = showAll ? plays : plays.slice(0, maxPlays);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-white/10 overflow-hidden"
      data-testid="play-by-play-widget"
    >
      {/* Header */}
      <div 
        className="bg-black/50 px-4 py-3 border-b border-white/10 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <List className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">PLAY-BY-PLAY</span>
            {isLive && (
              <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded animate-pulse">LIVE</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <a 
              href={statsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"
            >
              Full Log <ExternalLink className="w-3 h-3" />
            </a>
            {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </div>
        </div>
      </div>

      {/* Play List */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="max-h-96 overflow-y-auto">
              {displayedPlays.map((play, idx) => (
                <PlayItem 
                  key={play.id || idx} 
                  play={play} 
                  sport={sport}
                  homeTeam={homeTeam}
                  awayTeam={awayTeam}
                  isLatest={idx === 0}
                />
              ))}
            </div>

            {/* Show More/Less */}
            {plays.length > maxPlays && (
              <div className="p-2 border-t border-white/10 text-center">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs text-orange-500 hover:text-orange-400"
                >
                  {showAll ? `Show Less` : `Show All ${plays.length} Plays`}
                </button>
              </div>
            )}

            {/* Last Update */}
            {lastUpdate && (
              <div className="px-4 py-2 border-t border-white/10 text-xs text-zinc-500 text-right">
                Updated {lastUpdate.toLocaleTimeString()}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed Preview - Show last 3 plays */}
      {!expanded && plays.length > 0 && (
        <div className="p-2">
          {plays.slice(0, 3).map((play, idx) => (
            <PlayItemCompact key={play.id || idx} play={play} sport={sport} homeTeam={homeTeam} awayTeam={awayTeam} />
          ))}
          {plays.length > 3 && (
            <button 
              onClick={() => setExpanded(true)}
              className="w-full text-center text-xs text-zinc-500 hover:text-zinc-400 py-1"
            >
              +{plays.length - 3} more plays
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Individual Play Item (Expanded View)
const PlayItem = ({ play, sport, homeTeam, awayTeam, isLatest }) => {
  const getTypeColor = (type) => {
    switch (type) {
      case 'scoring': return 'border-l-green-500 bg-green-500/5';
      case 'turnover': return 'border-l-red-500 bg-red-500/5';
      case 'penalty': return 'border-l-yellow-500 bg-yellow-500/5';
      case 'timeout': return 'border-l-blue-500 bg-blue-500/5';
      case 'special': return 'border-l-purple-500 bg-purple-500/5';
      default: return 'border-l-zinc-600 bg-transparent';
    }
  };

  // Helper to normalize team data (handle both string and object formats)
  const normalizeTeam = (team) => {
    if (!team) return null;
    if (typeof team === 'string') {
      return { 
        name: team, 
        abbreviation: team.substring(0, 3).toUpperCase(),
        color: null,
        logo_url: null
      };
    }
    return team;
  };

  const getTeamInfo = () => {
    if (!play.team) return null;
    
    const teamRaw = play.team.toUpperCase().trim();
    const teamAbbr = teamRaw.slice(0, 3);
    
    // Normalize team props to handle both string and object formats
    const homeTeamNorm = normalizeTeam(homeTeam);
    const awayTeamNorm = normalizeTeam(awayTeam);
    
    // Handle generic "HOME" or "AWAY" identifiers from stat providers
    if (teamRaw === "HOME" || teamRaw === "H") {
      if (homeTeamNorm) {
        return { 
          name: homeTeamNorm.abbreviation || homeTeamNorm.name?.substring(0, 3)?.toUpperCase() || "HOME", 
          color: homeTeamNorm.color || play.teamColor,
          logo: homeTeamNorm.logo_url
        };
      }
    }
    if (teamRaw === "AWAY" || teamRaw === "VISITOR" || teamRaw === "V" || teamRaw === "A") {
      if (awayTeamNorm) {
        return { 
          name: awayTeamNorm.abbreviation || awayTeamNorm.name?.substring(0, 3)?.toUpperCase() || "AWAY", 
          color: awayTeamNorm.color || play.teamColor,
          logo: awayTeamNorm.logo_url
        };
      }
    }
    
    // Try to match with broadcast teams by abbreviation or name
    if (homeTeamNorm?.abbreviation?.toUpperCase() === teamAbbr || 
        homeTeamNorm?.name?.toUpperCase().includes(teamRaw)) {
      return { 
        name: homeTeamNorm.abbreviation || teamAbbr, 
        color: homeTeamNorm.color || play.teamColor,
        logo: homeTeamNorm.logo_url
      };
    }
    if (awayTeamNorm?.abbreviation?.toUpperCase() === teamAbbr ||
        awayTeamNorm?.name?.toUpperCase().includes(teamRaw)) {
      return { 
        name: awayTeamNorm.abbreviation || teamAbbr, 
        color: awayTeamNorm.color || play.teamColor,
        logo: awayTeamNorm.logo_url
      };
    }
    
    return { name: teamAbbr, color: play.teamColor, logo: null };
  };

  const teamInfo = getTeamInfo();

  return (
    <div 
      className={`px-4 py-3 border-l-4 ${getTypeColor(play.type)} ${isLatest ? 'bg-orange-500/5' : ''} border-b border-white/5 hover:bg-white/5 transition-colors`}
      data-testid="play-item"
    >
      <div className="flex items-start gap-3">
        {/* Team Badge */}
        {teamInfo && (
          <div 
            className="w-10 h-10 flex items-center justify-center text-xs font-bold rounded overflow-hidden"
            style={{ 
              backgroundColor: teamInfo.color ? `${teamInfo.color}20` : '#3f3f46',
              color: teamInfo.color || '#fff',
              border: `2px solid ${teamInfo.color || '#52525b'}`
            }}
          >
            {teamInfo.logo ? (
              <img src={teamInfo.logo} alt={teamInfo.name} className="w-7 h-7 object-contain" />
            ) : (
              teamInfo.name
            )}
          </div>
        )}
        
        {/* Play Details */}
        <div className="flex-1 min-w-0">
          {/* Time & Period */}
          <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
            {play.time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {play.time}
              </span>
            )}
            {play.period && (
              <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-400">
                {play.period}
              </span>
            )}
            {play.fieldPosition && sport === 'football' && (
              <span className="text-zinc-600">@ {play.fieldPosition}</span>
            )}
            {play.downDistance && (
              <span className="text-orange-500 font-medium">{play.downDistance}</span>
            )}
          </div>
          
          {/* Description */}
          <p className={`text-sm ${play.type === 'scoring' ? 'text-green-400 font-medium' : 'text-white'}`}>
            {play.description}
          </p>
          
          {/* Result */}
          {play.result && (
            <p className="text-xs text-zinc-500 mt-1">{play.result}</p>
          )}
        </div>

        {/* Points Badge */}
        {play.isScoring && play.points > 0 && (
          <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
            +{play.points}
          </div>
        )}
      </div>
    </div>
  );
};

// Compact Play Item (Collapsed View)
const PlayItemCompact = ({ play, sport, homeTeam, awayTeam }) => {
  // Helper to normalize team data (handle both string and object formats)
  const normalizeTeam = (team) => {
    if (!team) return null;
    if (typeof team === 'string') {
      return { 
        name: team, 
        abbreviation: team.substring(0, 3).toUpperCase(),
        color: null,
        logo_url: null
      };
    }
    return team;
  };

  // Resolve team name from generic identifiers
  const getDisplayTeam = () => {
    if (!play.team) return null;
    const teamRaw = play.team.toUpperCase().trim();
    
    // Normalize team props
    const homeTeamNorm = normalizeTeam(homeTeam);
    const awayTeamNorm = normalizeTeam(awayTeam);
    
    // Handle generic identifiers
    if (teamRaw === "HOME" || teamRaw === "H") {
      return homeTeamNorm?.abbreviation || homeTeamNorm?.name?.substring(0, 3)?.toUpperCase() || "HOME";
    }
    if (teamRaw === "AWAY" || teamRaw === "VISITOR" || teamRaw === "V" || teamRaw === "A") {
      return awayTeamNorm?.abbreviation || awayTeamNorm?.name?.substring(0, 3)?.toUpperCase() || "AWAY";
    }
    
    return play.team.slice(0, 3).toUpperCase();
  };

  const displayTeam = getDisplayTeam();

  return (
    <div className="flex items-center gap-2 py-1.5 px-2 text-xs border-b border-white/5 last:border-0">
      {displayTeam && (
        <span className="w-8 text-orange-500 font-bold">{displayTeam}</span>
      )}
      {play.time && (
        <span className="text-zinc-500 w-12">{play.time}</span>
      )}
      <span className="text-zinc-300 truncate flex-1">{play.description}</span>
      {play.isScoring && (
        <span className="text-green-500 font-bold">+{play.points || 'SCORE'}</span>
      )}
    </div>
  );
};

export default PlayByPlayWidget;
